import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';
import { useLocation, useSearchParams } from 'react-router-dom';
import { athleteAPI, equipmentAPI, trainingSessionAPI, bananaCupAPI } from '../../services/api';
import { Athlete } from '../../types/mongodb';
import { getQuickDateRange } from './dateRangeUtils';
import { loadAnalyticsFilters, saveAnalyticsFilters, getPagePathFromRoute } from '../../utils/analyticsFilterStorage';
import {
  AnalyticsContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  DataSelectionSection,
  SelectionGrid,
  SelectionGroup,
  GraphSection,
  GraphSectionWithPeriodSelector,
  GraphTitle,
  GraphContainer,
  EmptyState,
  PeriodSelectorTopRight,
  PeriodButtonWrapperSmall,
  PeriodButtonSmall,
  PeriodLabelSmall,
} from './styles';
import {
  DateRangeDropdown,
  AthletesDropdown,
  LocationDropdown,
  HillSizeDropdown,
  SessionTypesDropdown,
  EquipmentDropdown,
} from './FilterDropdowns';
import { getAthleteColor } from '../../utils/athleteColors';


// Custom bar shape with rounded top corners and shadow, with varying opacity
const RoundedBar = (props: any) => {
  const { fill, x, y, width, height, payload, index } = props;
  const radius = 8;
  
  // Calculate opacity based on index (each bar gets slightly less opacity)
  // Use index from props, or fallback to 0, with a minimum opacity of 0.6
  const barIndex = index !== undefined ? index : 0;
  const opacity = Math.max(0.6, 1.0 - barIndex * 0.03);
  
  // Create path for rectangle with only top corners rounded
  // Path starts from bottom-left, goes up, rounds top-left, across top, rounds top-right, down to bottom-right, then closes
  const path = `
    M ${x},${y + height}
    L ${x},${y + radius}
    Q ${x},${y} ${x + radius},${y}
    L ${x + width - radius},${y}
    Q ${x + width},${y} ${x + width},${y + radius}
    L ${x + width},${y + height}
    Z
  `;
  
  return (
    <g>
      <path
        d={path}
        fill={fill}
        fillOpacity={opacity}
        style={{
          filter: 'drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.15))',
        }}
      />
    </g>
  );
};


interface Equipment {
  _id: string;
  name: string;
  type: 'suit' | 'skis' | 'shoes' | 'bindings';
  brand?: string;
  size?: string;
  athleteId?: string | { _id: string; firstName: string; lastName: string };
}

interface TrainingSession {
  _id: string;
  date: string;
  location: string;
  hillSize?: string;
  trainingType: string;
  jumps?: Array<{
    athleteId: string | { _id: string; firstName: string; lastName: string };
    distance?: number;
    speed?: number;
    suitId: string;
    skisId: string;
    shoesId?: string;
    bindingsId?: string;
  }>;
  competitionJumps?: Array<{
    athleteId: string | { _id: string; firstName: string; lastName: string };
    firstRoundDistance?: number;
    secondRoundDistance?: number;
    firstRoundSpeed?: number;
    secondRoundSpeed?: number;
    suitId: string;
    skisId: string;
    shoesId?: string;
    bindingsId?: string;
  }>;
  groundTrainings?: Array<any>;
  crossCountryTrainings?: Array<any>;
}

type EquipmentType = 'suit' | 'skis' | 'shoes' | 'bindings';

interface EquipmentAnalyticsProps {
  isJumperMode?: boolean;
}

const EquipmentAnalytics: React.FC<EquipmentAnalyticsProps> = ({ isJumperMode = false }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pagePath = getPagePathFromRoute(location.pathname, searchParams);
  
  // Load saved filters on mount
  const savedFilters = loadAnalyticsFilters(pagePath);
  
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState<EquipmentType>((savedFilters?.activeTab as EquipmentType) || 'suit');
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [bananaCups, setBananaCups] = useState<any[]>([]);
  
  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [selectedAthletes, setSelectedAthletes] = useState<string[]>(savedFilters?.selectedAthletes || []);
  // Store selected equipment per tab
  const [selectedEquipmentByTab, setSelectedEquipmentByTab] = useState<Record<EquipmentType, string[]>>({
    suit: (savedFilters?.selectedEquipmentByTab as any)?.suit || savedFilters?.selectedEquipment || [],
    skis: (savedFilters?.selectedEquipmentByTab as any)?.skis || [],
    shoes: (savedFilters?.selectedEquipmentByTab as any)?.shoes || [],
    bindings: (savedFilters?.selectedEquipmentByTab as any)?.bindings || [],
  });
  // Current tab's selected equipment (derived from selectedEquipmentByTab)
  const selectedEquipment = selectedEquipmentByTab[activeTab];
  const [startDate, setStartDate] = useState<string>(savedFilters?.startDate || '');
  const [endDate, setEndDate] = useState<string>(savedFilters?.endDate || '');
  const [selectedLocation, setSelectedLocation] = useState<string>(savedFilters?.selectedLocation || '');
  const [selectedHillSize, setSelectedHillSize] = useState<string>(savedFilters?.selectedHillSize || '');
  const [selectedSessionTypes, setSelectedSessionTypes] = useState<string[]>(savedFilters?.selectedSessionTypes || ['ski_jumping', 'competition', 'banana_cup']);
  const [selectedPeriod, setSelectedPeriod] = useState<'daily' | 'weekly' | 'monthly'>(savedFilters?.selectedPeriod || 'daily');
  const [distanceDataDaily, setDistanceDataDaily] = useState<any[]>([]);
  const [distanceDataWeekly, setDistanceDataWeekly] = useState<any[]>([]);
  const [distanceDataMonthly, setDistanceDataMonthly] = useState<any[]>([]);
  const [jumpCountDataDaily, setJumpCountDataDaily] = useState<any[]>([]);
  const [jumpCountDataWeekly, setJumpCountDataWeekly] = useState<any[]>([]);
  const [jumpCountDataMonthly, setJumpCountDataMonthly] = useState<any[]>([]);
  const [speedDataDaily, setSpeedDataDaily] = useState<any[]>([]);
  const [speedDataWeekly, setSpeedDataWeekly] = useState<any[]>([]);
  const [speedDataMonthly, setSpeedDataMonthly] = useState<any[]>([]);
  const [usageData, setUsageData] = useState<any[]>([]);
  const [longestDistances, setLongestDistances] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [locations, setLocations] = useState<string[]>([]);
  const [hillSizes, setHillSizes] = useState<string[]>([]);
  const [jumpCounts, setJumpCounts] = useState<Record<string, number>>({});
  const [jumperAthleteId, setJumperAthleteId] = useState<string | null>(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // If jumper mode, get the logged-in jumper's athlete ID first
        if (isJumperMode) {
          try {
            const myAthlete = await athleteAPI.getMyAthlete();
            const athleteId = myAthlete._id.toString();
            setJumperAthleteId(athleteId);
            setSelectedAthletes([athleteId]);
          } catch (error) {
            console.error('Error loading jumper athlete:', error);
          }
        }
        
        const [athletesData, equipmentData, sessionsResponse, bananaCupsData, jumpCountsData] = await Promise.all([
          athleteAPI.getAll(),
          equipmentAPI.getAll(),
          trainingSessionAPI.getAll(undefined, undefined, true), // Load all sessions for analytics
          bananaCupAPI.getAll(),
          equipmentAPI.getJumpCounts(),
        ]);

        // Handle response - when all=true, returns array directly, otherwise paginated object
        const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse.sessions || []);

        setAthletes(athletesData);
        setEquipment(equipmentData);
        setSessions(sessionsData);
        setBananaCups(bananaCupsData || []);
        setJumpCounts(jumpCountsData || {});

        // Extract unique locations and hill sizes
        const uniqueLocations = Array.from(new Set(sessionsData.map((s: TrainingSession) => s.location).filter(Boolean)));
        const uniqueHillSizes = Array.from(new Set(
          sessionsData.flatMap((s: TrainingSession) => [
            s.hillSize,
            ...(s.jumps?.map(j => (j as any).hillSize) || []),
            ...(s.competitionJumps?.map(j => (j as any).hillSize) || []),
          ].filter(Boolean))
        ));

        setLocations(uniqueLocations as string[]);
        setHillSizes(uniqueHillSizes as string[]);
        
        // If jumper mode, ensure only the jumper's athlete is selected
        if (isJumperMode && jumperAthleteId) {
          setSelectedAthletes([jumperAthleteId]);
        }

        // Set default date range (last 3 months) only if no saved filters exist
        if (!savedFilters || (!savedFilters.startDate && !savedFilters.endDate)) {
          const today = new Date();
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(today.getMonth() - 3);
          setEndDate(today.toISOString().split('T')[0]);
          setStartDate(threeMonthsAgo.toISOString().split('T')[0]);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Save filters whenever they change (skip on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    saveAnalyticsFilters(pagePath, {
      selectedAthletes,
      selectedEquipmentByTab,
      startDate,
      endDate,
      selectedLocation,
      selectedHillSize,
      selectedSessionTypes,
      selectedPeriod,
      activeTab,
    });
  }, [pagePath, selectedAthletes, selectedEquipmentByTab, startDate, endDate, selectedLocation, selectedHillSize, selectedSessionTypes, selectedPeriod, activeTab]);

  // Filter equipment by active tab and selected athletes
  const filteredEquipment = equipment.filter(eq => {
    if (eq.type !== activeTab) return false;
    
    // If no athletes selected, don't show any equipment
    if (selectedAthletes.length === 0) return false;
    
    // Filter by selected athletes
    const athleteId = typeof eq.athleteId === 'object' ? eq.athleteId._id : eq.athleteId;
    return athleteId && selectedAthletes.includes(athleteId);
  });

  // Update selected equipment when tab changes (load saved equipment for that tab)
  // This effect runs when activeTab changes, but we don't need to do anything here
  // because selectedEquipment is derived from selectedEquipmentByTab[activeTab]

  // Clean up selected equipment when athletes change - only remove equipment that no longer belongs to selected athletes
  useEffect(() => {
    if (selectedAthletes.length === 0) {
      setSelectedEquipmentByTab({
        suit: [],
        skis: [],
        shoes: [],
        bindings: [],
      });
      return;
    }

    // Filter out equipment that no longer belongs to any selected athlete for all tabs
    setSelectedEquipmentByTab(prev => {
      const updated: Record<EquipmentType, string[]> = {
        suit: [],
        skis: [],
        shoes: [],
        bindings: [],
      };
      
      (['suit', 'skis', 'shoes', 'bindings'] as EquipmentType[]).forEach(tab => {
        updated[tab] = prev[tab].filter(equipmentId => {
          const equipmentItem = equipment.find(eq => String(eq._id) === equipmentId);
          if (!equipmentItem) return false;
          
          const athleteId = typeof equipmentItem.athleteId === 'object' 
            ? equipmentItem.athleteId._id 
            : equipmentItem.athleteId;
          
          // Keep equipment if it belongs to one of the selected athletes
          return athleteId && selectedAthletes.includes(athleteId);
        });
      });
      
      return updated;
    });
  }, [selectedAthletes, equipment]);

  const processData = () => {
    if (!startDate || !endDate || selectedEquipment.length === 0) {
      setDistanceDataDaily([]);
      setDistanceDataWeekly([]);
      setDistanceDataMonthly([]);
      setJumpCountDataDaily([]);
      setJumpCountDataWeekly([]);
      setJumpCountDataMonthly([]);
      setSpeedDataDaily([]);
      setSpeedDataWeekly([]);
      setSpeedDataMonthly([]);
      setUsageData([]);
      setLongestDistances([]);
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    // Helper function to check if a session has been recorded (has actual data)
    const isRecordedSession = (session: TrainingSession): boolean => {
      const hasJumps = !!(session.jumps && session.jumps.length > 0);
      const hasCompetitionJumps = !!(session.competitionJumps && session.competitionJumps.length > 0);
      const hasGroundTrainings = !!(session.groundTrainings && session.groundTrainings.length > 0);
      const hasCrossCountryTrainings = !!(session.crossCountryTrainings && session.crossCountryTrainings.length > 0);
      return hasJumps || hasCompetitionJumps || hasGroundTrainings || hasCrossCountryTrainings;
    };

    // Filter sessions by date range, location, and training type
    // Also exclude pre-planned sessions (sessions with no actual data)
    const filteredSessions = sessions.filter(session => {
      const sessionDate = new Date(session.date);
      if (sessionDate < start || sessionDate > end) return false;
      if (selectedLocation && session.location !== selectedLocation) return false;
      // Filter by selected session types
      if (!selectedSessionTypes.includes(session.trainingType)) return false;
      // Exclude pre-planned sessions (only include sessions with actual data)
      if (!isRecordedSession(session)) return false;
      return true;
    });

    // Process jumps for distance data and jump count data
    const distanceMap: Map<string, { date: string; [key: string]: any }> = new Map();
    const jumpCountMap: Map<string, { date: string; [key: string]: any }> = new Map();
    const speedMap: Map<string, { date: string; [key: string]: any }> = new Map();
    const usageCount: Map<string, number> = new Map();
    const longestDistancesMap: Map<string, { distance: number; date: string; location: string; athlete: string }> = new Map();

    filteredSessions.forEach(session => {
      // Process regular jumps
      if (session.jumps) {
        session.jumps.forEach(jump => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          // Check if athlete is selected
          if (selectedAthletes.length > 0 && !selectedAthletes.includes(athleteId)) return;

          // Check equipment based on active tab
          let equipmentId = '';
          if (activeTab === 'suit') equipmentId = jump.suitId;
          else if (activeTab === 'skis') equipmentId = jump.skisId;
          else if (activeTab === 'shoes' && jump.shoesId) equipmentId = jump.shoesId;
          else if (activeTab === 'bindings' && jump.bindingsId) equipmentId = jump.bindingsId;

          // Convert equipmentId to string for comparison
          const equipmentIdStr = typeof equipmentId === 'object' ? (equipmentId as any)._id || equipmentId : String(equipmentId);
          if (!equipmentIdStr || !selectedEquipment.includes(equipmentIdStr)) return;

          // Check hill size if selected
          const jumpHillSize = (jump as any).hillSize || session.hillSize;
          if (selectedHillSize && jumpHillSize !== selectedHillSize) return;

          if (jump.distance) {
            const dateKey = session.date;
            const equipment = filteredEquipment.find(eq => {
              const eqId = String(eq._id);
              return eqId === equipmentIdStr;
            });
            const equipmentName = equipment?.name || 'Unknown';

            // Distance data for line chart
            if (!distanceMap.has(dateKey)) {
              distanceMap.set(dateKey, { date: dateKey });
            }
            const dataPoint = distanceMap.get(dateKey)!;
            if (!dataPoint[equipmentName]) {
              dataPoint[equipmentName] = { sum: 0, count: 0 };
            }
            dataPoint[equipmentName].sum += jump.distance;
            dataPoint[equipmentName].count += 1;

            // Jump count data for line chart
            if (!jumpCountMap.has(dateKey)) {
              jumpCountMap.set(dateKey, { date: dateKey });
            }
            const jumpCountPoint = jumpCountMap.get(dateKey)!;
            if (!jumpCountPoint[equipmentName]) {
              jumpCountPoint[equipmentName] = 0;
            }
            jumpCountPoint[equipmentName] += 1;

            // Speed data for line chart (only for skis tab)
            if (activeTab === 'skis' && (jump as any).speed) {
              if (!speedMap.has(dateKey)) {
                speedMap.set(dateKey, { date: dateKey });
              }
              const speedPoint = speedMap.get(dateKey)!;
              if (!speedPoint[equipmentName]) {
                speedPoint[equipmentName] = { sum: 0, count: 0 };
              }
              speedPoint[equipmentName].sum += (jump as any).speed;
              speedPoint[equipmentName].count += 1;
            }

            // Usage count
            usageCount.set(equipmentIdStr, (usageCount.get(equipmentIdStr) || 0) + 1);

            // Longest distances
            const currentLongest = longestDistancesMap.get(equipmentIdStr);
            if (!currentLongest || jump.distance > currentLongest.distance) {
              const athlete = athletes.find(a => a._id === athleteId);
              longestDistancesMap.set(equipmentIdStr, {
                distance: jump.distance,
                date: session.date,
                location: session.location,
                athlete: athlete ? `${athlete.firstName} ${athlete.lastName}` : 'Unknown',
              });
            }
          }
        });
      }

      // Process competition jumps
      if (session.competitionJumps) {
        session.competitionJumps.forEach(jump => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          if (selectedAthletes.length > 0 && !selectedAthletes.includes(athleteId)) return;

          const jumpHillSize = (jump as any).hillSize || session.hillSize;
          if (selectedHillSize && jumpHillSize !== selectedHillSize) return;

          // Process all rounds (trial round, competition rounds, banana cup can have up to 5 rounds)
          const rounds = [
            { distance: (jump as any).trialRoundDistance, speed: (jump as any).trialRoundSpeed, suitId: (jump as any).trialRoundSuitId, skisId: (jump as any).trialRoundSkisId, shoesId: (jump as any).trialRoundShoesId, bindingsId: (jump as any).trialRoundBindingsId },
            { distance: jump.firstRoundDistance, speed: jump.firstRoundSpeed, suitId: (jump as any).firstRoundSuitId, skisId: (jump as any).firstRoundSkisId, shoesId: (jump as any).firstRoundShoesId, bindingsId: (jump as any).firstRoundBindingsId },
            { distance: jump.secondRoundDistance, speed: jump.secondRoundSpeed, suitId: (jump as any).secondRoundSuitId, skisId: (jump as any).secondRoundSkisId, shoesId: (jump as any).secondRoundShoesId, bindingsId: (jump as any).secondRoundBindingsId },
            { distance: (jump as any).thirdRoundDistance, speed: (jump as any).thirdRoundSpeed, suitId: (jump as any).thirdRoundSuitId, skisId: (jump as any).thirdRoundSkisId, shoesId: (jump as any).thirdRoundShoesId, bindingsId: (jump as any).thirdRoundBindingsId },
            { distance: (jump as any).fourthRoundDistance, speed: (jump as any).fourthRoundSpeed, suitId: (jump as any).fourthRoundSuitId, skisId: (jump as any).fourthRoundSkisId, shoesId: (jump as any).fourthRoundShoesId, bindingsId: (jump as any).fourthRoundBindingsId },
            { distance: (jump as any).fifthRoundDistance, speed: (jump as any).fifthRoundSpeed, suitId: (jump as any).fifthRoundSuitId, skisId: (jump as any).fifthRoundSkisId, shoesId: (jump as any).fifthRoundShoesId, bindingsId: (jump as any).fifthRoundBindingsId },
          ].filter(r => r.distance);

          rounds.forEach(round => {
            let equipmentId = '';
            if (activeTab === 'suit') equipmentId = round.suitId || (jump as any).suitId;
            else if (activeTab === 'skis') equipmentId = round.skisId || (jump as any).skisId;
            else if (activeTab === 'shoes') equipmentId = round.shoesId || (jump as any).shoesId;
            else if (activeTab === 'bindings') equipmentId = round.bindingsId || (jump as any).bindingsId;

            const equipmentIdStr = equipmentId ? (typeof equipmentId === 'object' ? (equipmentId as any)._id || equipmentId : String(equipmentId)) : '';
            if (equipmentIdStr && selectedEquipment.includes(equipmentIdStr)) {
              const dateKey = session.date;
              const equipment = filteredEquipment.find(eq => String(eq._id) === equipmentIdStr);
              const equipmentName = equipment?.name || 'Unknown';

              if (!distanceMap.has(dateKey)) {
                distanceMap.set(dateKey, { date: dateKey });
              }
              const dataPoint = distanceMap.get(dateKey)!;
              if (!dataPoint[equipmentName]) {
                dataPoint[equipmentName] = { sum: 0, count: 0 };
              }
              dataPoint[equipmentName].sum += round.distance;
              dataPoint[equipmentName].count += 1;

              // Jump count data
              if (!jumpCountMap.has(dateKey)) {
                jumpCountMap.set(dateKey, { date: dateKey });
              }
              const jumpCountPoint = jumpCountMap.get(dateKey)!;
              if (!jumpCountPoint[equipmentName]) {
                jumpCountPoint[equipmentName] = 0;
              }
              jumpCountPoint[equipmentName] += 1;

              // Speed data for line chart (only for skis tab)
              if (activeTab === 'skis' && round.speed) {
                if (!speedMap.has(dateKey)) {
                  speedMap.set(dateKey, { date: dateKey });
                }
                const speedPoint = speedMap.get(dateKey)!;
                if (!speedPoint[equipmentName]) {
                  speedPoint[equipmentName] = { sum: 0, count: 0 };
                }
                speedPoint[equipmentName].sum += round.speed;
                speedPoint[equipmentName].count += 1;
              }

              usageCount.set(equipmentIdStr, (usageCount.get(equipmentIdStr) || 0) + 1);

              const currentLongest = longestDistancesMap.get(equipmentIdStr);
              if (!currentLongest || round.distance > currentLongest.distance) {
                const athlete = athletes.find(a => a._id === athleteId);
                longestDistancesMap.set(equipmentIdStr, {
                  distance: round.distance,
                  date: session.date,
                  location: session.location,
                  athlete: athlete ? `${athlete.firstName} ${athlete.lastName}` : 'Unknown',
                });
              }
            }
          });
        });
      }
    });

    // Process banana cup jumps (only if banana_cup is selected)
    if (selectedSessionTypes.includes('banana_cup')) {
      bananaCups.forEach(bananaCup => {
        if (!bananaCup.competitions || !Array.isArray(bananaCup.competitions)) return;
        
        bananaCup.competitions.forEach((competition: any) => {
          if (!competition.isCompleted || !competition.jumps || !Array.isArray(competition.jumps)) return;
          
          // Check if competition date is in range
          const competitionDate = new Date(competition.date);
          if (competitionDate < start || competitionDate > end) return;
          if (selectedLocation && competition.location !== selectedLocation) return;
          
          competition.jumps.forEach((jump: any) => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          if (selectedAthletes.length > 0 && !selectedAthletes.includes(athleteId)) return;
          
          const jumpHillSize = jump.hillSize || competition.hillSize;
          if (selectedHillSize && jumpHillSize !== selectedHillSize) return;

          // Process all rounds (banana cup can have up to 5 rounds)
          const rounds = [
            { distance: jump.firstRoundDistance, speed: jump.firstRoundSpeed, suitId: jump.suitId, skisId: jump.skisId, shoesId: jump.shoesId, bindingsId: jump.bindingsId },
            { distance: jump.secondRoundDistance, speed: jump.secondRoundSpeed, suitId: jump.suitId, skisId: jump.skisId, shoesId: jump.shoesId, bindingsId: jump.bindingsId },
            { distance: jump.thirdRoundDistance, speed: jump.thirdRoundSpeed, suitId: jump.suitId, skisId: jump.skisId, shoesId: jump.shoesId, bindingsId: jump.bindingsId },
            { distance: jump.fourthRoundDistance, speed: jump.fourthRoundSpeed, suitId: jump.suitId, skisId: jump.skisId, shoesId: jump.shoesId, bindingsId: jump.bindingsId },
            { distance: jump.fifthRoundDistance, speed: jump.fifthRoundSpeed, suitId: jump.suitId, skisId: jump.skisId, shoesId: jump.shoesId, bindingsId: jump.bindingsId },
          ].filter(r => r.distance);

          rounds.forEach(round => {
            let equipmentId = '';
            if (activeTab === 'suit') equipmentId = round.suitId;
            else if (activeTab === 'skis') equipmentId = round.skisId;
            else if (activeTab === 'shoes') equipmentId = round.shoesId;
            else if (activeTab === 'bindings') equipmentId = round.bindingsId;

            const equipmentIdStr = equipmentId ? (typeof equipmentId === 'object' ? (equipmentId as any)._id || equipmentId : String(equipmentId)) : '';
            if (equipmentIdStr && selectedEquipment.includes(equipmentIdStr)) {
              const dateKey = competition.date;
              const equipment = filteredEquipment.find(eq => String(eq._id) === equipmentIdStr);
              const equipmentName = equipment?.name || 'Unknown';

              if (!distanceMap.has(dateKey)) {
                distanceMap.set(dateKey, { date: dateKey });
              }
              const dataPoint = distanceMap.get(dateKey)!;
              if (!dataPoint[equipmentName]) {
                dataPoint[equipmentName] = { sum: 0, count: 0 };
              }
              dataPoint[equipmentName].sum += round.distance;
              dataPoint[equipmentName].count += 1;

              // Jump count data
              if (!jumpCountMap.has(dateKey)) {
                jumpCountMap.set(dateKey, { date: dateKey });
              }
              const jumpCountPoint = jumpCountMap.get(dateKey)!;
              if (!jumpCountPoint[equipmentName]) {
                jumpCountPoint[equipmentName] = 0;
              }
              jumpCountPoint[equipmentName] += 1;

              // Speed data for line chart (only for skis tab)
              if (activeTab === 'skis' && round.speed) {
                if (!speedMap.has(dateKey)) {
                  speedMap.set(dateKey, { date: dateKey });
                }
                const speedPoint = speedMap.get(dateKey)!;
                if (!speedPoint[equipmentName]) {
                  speedPoint[equipmentName] = { sum: 0, count: 0 };
                }
                speedPoint[equipmentName].sum += round.speed;
                speedPoint[equipmentName].count += 1;
              }

              usageCount.set(equipmentIdStr, (usageCount.get(equipmentIdStr) || 0) + 1);

              const currentLongest = longestDistancesMap.get(equipmentIdStr);
              if (!currentLongest || round.distance > currentLongest.distance) {
                const athlete = athletes.find(a => a._id === athleteId);
                longestDistancesMap.set(equipmentIdStr, {
                  distance: round.distance,
                  date: competition.date,
                  location: competition.location,
                  athlete: athlete ? `${athlete.firstName} ${athlete.lastName}` : 'Unknown',
                });
              }
            }
          });
          });
        });
      });
    }

    // Daily distance data
    const processedDistanceDataDaily: any[] = [];
    const currentDate = new Date(start);
    while (currentDate <= end) {
      const dateKey = currentDate.toISOString().split('T')[0];
      const dataPoint: any = { date: dateKey };
      
      selectedEquipment.forEach(equipmentId => {
        const equipment = filteredEquipment.find(eq => String(eq._id) === equipmentId);
        if (equipment) {
          const point = distanceMap.get(dateKey);
          if (point && point[equipment.name] && point[equipment.name].count > 0) {
            dataPoint[equipment.name] = point[equipment.name].sum / point[equipment.name].count;
          } else {
            dataPoint[equipment.name] = null;
          }
        }
      });
      
      processedDistanceDataDaily.push(dataPoint);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Weekly distance data
    const processedDistanceDataWeekly: any[] = [];
    const weekStart = new Date(start);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1); // Start of week (Monday)
    
    while (weekStart <= end) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const weekKey = weekStart.toISOString().split('T')[0];
      const dataPoint: any = { week: weekKey };
      
      selectedEquipment.forEach(equipmentId => {
        const equipment = filteredEquipment.find(eq => String(eq._id) === equipmentId);
        if (equipment) {
          const weekDistances: number[] = [];
          const checkDate = new Date(weekStart);
          while (checkDate <= weekEnd && checkDate <= end) {
            const dateStr = checkDate.toISOString().split('T')[0];
            const point = distanceMap.get(dateStr);
            if (point && point[equipment.name] && point[equipment.name].count > 0) {
              weekDistances.push(point[equipment.name].sum / point[equipment.name].count);
            }
            checkDate.setDate(checkDate.getDate() + 1);
          }
          
          if (weekDistances.length > 0) {
            const avg = weekDistances.reduce((a, b) => a + b, 0) / weekDistances.length;
            dataPoint[equipment.name] = Math.round(avg * 10) / 10;
          } else {
            dataPoint[equipment.name] = null;
          }
        }
      });
      
      processedDistanceDataWeekly.push(dataPoint);
      weekStart.setDate(weekStart.getDate() + 7);
    }

    // Monthly distance data
    const processedDistanceDataMonthly: any[] = [];
    const monthStart = new Date(start.getFullYear(), start.getMonth(), 1);
    
    while (monthStart <= end) {
      const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
      const monthKey = `${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const dataPoint: any = { month: monthLabel };
      
      selectedEquipment.forEach(equipmentId => {
        const equipment = filteredEquipment.find(eq => String(eq._id) === equipmentId);
        if (equipment) {
          const monthDistances: number[] = [];
          const checkDate = new Date(monthStart);
          while (checkDate <= monthEnd && checkDate <= end) {
            const dateStr = checkDate.toISOString().split('T')[0];
            const point = distanceMap.get(dateStr);
            if (point && point[equipment.name] && point[equipment.name].count > 0) {
              monthDistances.push(point[equipment.name].sum / point[equipment.name].count);
            }
            checkDate.setDate(checkDate.getDate() + 1);
          }
          
          if (monthDistances.length > 0) {
            const avg = monthDistances.reduce((a, b) => a + b, 0) / monthDistances.length;
            dataPoint[equipment.name] = Math.round(avg * 10) / 10;
          } else {
            dataPoint[equipment.name] = null;
          }
        }
      });
      
      processedDistanceDataMonthly.push(dataPoint);
      monthStart.setMonth(monthStart.getMonth() + 1);
    }

    setDistanceDataDaily(processedDistanceDataDaily);
    setDistanceDataWeekly(processedDistanceDataWeekly);
    setDistanceDataMonthly(processedDistanceDataMonthly);

    // Daily jump count data
    const processedJumpCountDataDaily: any[] = [];
    const currentDateForJumps = new Date(start);
    
    while (currentDateForJumps <= end) {
      const dateKey = currentDateForJumps.toISOString().split('T')[0];
      const dataPoint: any = { date: dateKey };
      
      selectedEquipment.forEach(equipmentId => {
        const equipment = filteredEquipment.find(eq => String(eq._id) === equipmentId);
        if (equipment) {
          const point = jumpCountMap.get(dateKey);
          if (point && point[equipment.name] !== undefined) {
            dataPoint[equipment.name] = point[equipment.name];
          } else {
            dataPoint[equipment.name] = null;
          }
        }
      });
      
      processedJumpCountDataDaily.push(dataPoint);
      currentDateForJumps.setDate(currentDateForJumps.getDate() + 1);
    }

    // Weekly jump count data
    const processedJumpCountDataWeekly: any[] = [];
    const weekStartForJumps = new Date(start);
    weekStartForJumps.setDate(weekStartForJumps.getDate() - weekStartForJumps.getDay());
    
    while (weekStartForJumps <= end) {
      const weekEndForJumps = new Date(weekStartForJumps);
      weekEndForJumps.setDate(weekEndForJumps.getDate() + 6);
      const weekKey = weekStartForJumps.toISOString().split('T')[0];
      const dataPoint: any = { week: weekKey };
      
      selectedEquipment.forEach(equipmentId => {
        const equipment = filteredEquipment.find(eq => String(eq._id) === equipmentId);
        if (equipment) {
          let weekCount = 0;
          const checkDate = new Date(weekStartForJumps);
          while (checkDate <= weekEndForJumps && checkDate <= end) {
            const dateStr = checkDate.toISOString().split('T')[0];
            const point = jumpCountMap.get(dateStr);
            if (point && point[equipment.name] !== undefined) {
              weekCount += point[equipment.name];
            }
            checkDate.setDate(checkDate.getDate() + 1);
          }
          
          dataPoint[equipment.name] = weekCount > 0 ? weekCount : null;
        }
      });
      
      processedJumpCountDataWeekly.push(dataPoint);
      weekStartForJumps.setDate(weekStartForJumps.getDate() + 7);
    }

    // Monthly jump count data
    const processedJumpCountDataMonthly: any[] = [];
    const monthStartForJumps = new Date(start.getFullYear(), start.getMonth(), 1);
    
    while (monthStartForJumps <= end) {
      const monthEndForJumps = new Date(monthStartForJumps.getFullYear(), monthStartForJumps.getMonth() + 1, 0);
      const monthKey = `${monthStartForJumps.getFullYear()}-${String(monthStartForJumps.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = monthStartForJumps.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const dataPoint: any = { month: monthLabel };
      
      selectedEquipment.forEach(equipmentId => {
        const equipment = filteredEquipment.find(eq => String(eq._id) === equipmentId);
        if (equipment) {
          let monthCount = 0;
          const checkDate = new Date(monthStartForJumps);
          while (checkDate <= monthEndForJumps && checkDate <= end) {
            const dateStr = checkDate.toISOString().split('T')[0];
            const point = jumpCountMap.get(dateStr);
            if (point && point[equipment.name] !== undefined) {
              monthCount += point[equipment.name];
            }
            checkDate.setDate(checkDate.getDate() + 1);
          }
          
          dataPoint[equipment.name] = monthCount > 0 ? monthCount : null;
        }
      });
      
      processedJumpCountDataMonthly.push(dataPoint);
      monthStartForJumps.setMonth(monthStartForJumps.getMonth() + 1);
    }

    setJumpCountDataDaily(processedJumpCountDataDaily);
    setJumpCountDataWeekly(processedJumpCountDataWeekly);
    setJumpCountDataMonthly(processedJumpCountDataMonthly);

    // Daily speed data (only for skis tab)
    if (activeTab === 'skis') {
      const processedSpeedDataDaily: any[] = [];
      const currentDateForSpeed = new Date(start);
      
      while (currentDateForSpeed <= end) {
        const dateKey = currentDateForSpeed.toISOString().split('T')[0];
        const dataPoint: any = { date: dateKey };
        
        selectedEquipment.forEach(equipmentId => {
          const equipment = filteredEquipment.find(eq => String(eq._id) === equipmentId);
          if (equipment) {
            const point = speedMap.get(dateKey);
            if (point && point[equipment.name] && point[equipment.name].count > 0) {
              dataPoint[equipment.name] = Math.round((point[equipment.name].sum / point[equipment.name].count) * 10) / 10;
            } else {
              dataPoint[equipment.name] = null;
            }
          }
        });
        
        processedSpeedDataDaily.push(dataPoint);
        currentDateForSpeed.setDate(currentDateForSpeed.getDate() + 1);
      }

      // Weekly speed data
      const processedSpeedDataWeekly: any[] = [];
      const weekStartForSpeed = new Date(start);
      weekStartForSpeed.setDate(weekStartForSpeed.getDate() - weekStartForSpeed.getDay());
      
      while (weekStartForSpeed <= end) {
        const weekEndForSpeed = new Date(weekStartForSpeed);
        weekEndForSpeed.setDate(weekEndForSpeed.getDate() + 6);
        const weekKey = weekStartForSpeed.toISOString().split('T')[0];
        const dataPoint: any = { week: weekKey };
        
        selectedEquipment.forEach(equipmentId => {
          const equipment = filteredEquipment.find(eq => String(eq._id) === equipmentId);
          if (equipment) {
            const weekSpeeds: number[] = [];
            const checkDate = new Date(weekStartForSpeed);
            while (checkDate <= weekEndForSpeed && checkDate <= end) {
              const dateStr = checkDate.toISOString().split('T')[0];
              const point = speedMap.get(dateStr);
              if (point && point[equipment.name] && point[equipment.name].count > 0) {
                weekSpeeds.push(point[equipment.name].sum / point[equipment.name].count);
              }
              checkDate.setDate(checkDate.getDate() + 1);
            }
            
            if (weekSpeeds.length > 0) {
              const avg = weekSpeeds.reduce((a, b) => a + b, 0) / weekSpeeds.length;
              dataPoint[equipment.name] = Math.round(avg * 10) / 10;
            } else {
              dataPoint[equipment.name] = null;
            }
          }
        });
        
        processedSpeedDataWeekly.push(dataPoint);
        weekStartForSpeed.setDate(weekStartForSpeed.getDate() + 7);
      }

      // Monthly speed data
      const processedSpeedDataMonthly: any[] = [];
      const monthStartForSpeed = new Date(start.getFullYear(), start.getMonth(), 1);
      
      while (monthStartForSpeed <= end) {
        const monthEndForSpeed = new Date(monthStartForSpeed.getFullYear(), monthStartForSpeed.getMonth() + 1, 0);
        const monthKey = `${monthStartForSpeed.getFullYear()}-${String(monthStartForSpeed.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = monthStartForSpeed.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        const dataPoint: any = { month: monthLabel };
        
        selectedEquipment.forEach(equipmentId => {
          const equipment = filteredEquipment.find(eq => String(eq._id) === equipmentId);
          if (equipment) {
            const monthSpeeds: number[] = [];
            const checkDate = new Date(monthStartForSpeed);
            while (checkDate <= monthEndForSpeed && checkDate <= end) {
              const dateStr = checkDate.toISOString().split('T')[0];
              const point = speedMap.get(dateStr);
              if (point && point[equipment.name] && point[equipment.name].count > 0) {
                monthSpeeds.push(point[equipment.name].sum / point[equipment.name].count);
              }
              checkDate.setDate(checkDate.getDate() + 1);
            }
            
            if (monthSpeeds.length > 0) {
              const avg = monthSpeeds.reduce((a, b) => a + b, 0) / monthSpeeds.length;
              dataPoint[equipment.name] = Math.round(avg * 10) / 10;
            } else {
              dataPoint[equipment.name] = null;
            }
          }
        });
        
        processedSpeedDataMonthly.push(dataPoint);
        monthStartForSpeed.setMonth(monthStartForSpeed.getMonth() + 1);
      }

      setSpeedDataDaily(processedSpeedDataDaily);
      setSpeedDataWeekly(processedSpeedDataWeekly);
      setSpeedDataMonthly(processedSpeedDataMonthly);
    } else {
      setSpeedDataDaily([]);
      setSpeedDataWeekly([]);
      setSpeedDataMonthly([]);
    }

    // Process usage data for bar chart (top 10)
    const usageArray = Array.from(usageCount.entries())
      .map(([equipmentId, count]) => {
        const equipmentItem = filteredEquipment.find(eq => eq._id === equipmentId);
        return {
          name: equipmentItem?.name || 'Unknown',
          count: count,
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    setUsageData(usageArray);

    // Process longest distances
    const longestArray = Array.from(longestDistancesMap.entries())
      .map(([equipmentId, data]) => {
        const equipmentItem = filteredEquipment.find(eq => eq._id === equipmentId);
        return {
          equipmentName: equipmentItem?.name || 'Unknown',
          distance: data.distance,
          date: data.date,
          location: data.location,
          athlete: data.athlete,
        };
      })
      .sort((a, b) => b.distance - a.distance);

    setLongestDistances(longestArray);
  };

  useEffect(() => {
    processData();
  }, [activeTab, selectedAthletes, selectedEquipment, startDate, endDate, selectedLocation, selectedHillSize, sessions, equipment, athletes, bananaCups, selectedSessionTypes]);

  const handleAthleteToggle = (athleteId: string) => {
    setSelectedAthletes(prev =>
      prev.includes(athleteId)
        ? prev.filter(id => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  const handleEquipmentToggle = (equipmentId: string) => {
    const equipmentIdStr = String(equipmentId);
    setSelectedEquipmentByTab(prev => ({
      ...prev,
      [activeTab]: prev[activeTab].includes(equipmentIdStr)
        ? prev[activeTab].filter(id => id !== equipmentIdStr)
        : [...prev[activeTab], equipmentIdStr]
    }));
  };

  const handleUpdateDate = () => {
    processData();
  };

  const handleQuickDateRange = (range: string) => {
    const { startDate: newStartDate, endDate: newEndDate } = getQuickDateRange(range);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleSessionTypeToggle = (sessionType: string) => {
    setSelectedSessionTypes(prev =>
      prev.includes(sessionType)
        ? prev.filter(type => type !== sessionType)
        : [...prev, sessionType]
    );
  };

  const handleAthletesSave = (selected: string[]) => {
    setSelectedAthletes(selected);
  };

  const handleEquipmentSave = (selected: string[]) => {
    setSelectedEquipmentByTab(prev => ({
      ...prev,
      [activeTab]: selected
    }));
  };

  const handleSessionTypesSave = (types: string[]) => {
    setSelectedSessionTypes(types);
  };

  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    if (dateString.includes('-') && dateString.length === 10) {
      return dateString;
    }
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  // Default colors for equipment (equipment doesn't have assigned colors like athletes)
  const getEquipmentColors = () => {
    return [
      '#ff9600', '#0066cc', '#00cc66', '#cc0066', '#6600cc',
      '#cc6600', '#00cccc', '#cc0066', '#0066ff', '#ff6600',
    ];
  };

  // Helper function to calculate Y-axis domain (min - 10, auto max)
  const calculateYAxisDomain = (data: any[], dataKeys: string[]): [number, string] => {
    let minValue = Infinity;
    
    data.forEach(point => {
      dataKeys.forEach(key => {
        const value = point[key];
        if (value !== null && value !== undefined && typeof value === 'number' && value < minValue) {
          minValue = value;
        }
      });
    });
    
    if (minValue === Infinity) {
      return [0, 'auto'];
    }
    
    return [Math.max(0, minValue - 10), 'auto'];
  };

  if (isLoading) {
    return (
      <AnalyticsContainer>
        <EmptyState>Loading equipment data...</EmptyState>
      </AnalyticsContainer>
    );
  }

  return (
    <AnalyticsContainer>
      <PageHeader>
        <PageTitle>Equipment Analytics</PageTitle>
        <PageDescription>
          Analyze equipment usage, performance correlations, and equipment-related metrics. Compare equipment performance across different conditions and time periods.
        </PageDescription>
      </PageHeader>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: isMobile ? '0.25rem' : '1rem', 
        marginBottom: '1rem', 
        borderBottom: isMobile ? '1px solid var(--card-border)' : '2px solid var(--card-border)',
        overflowX: isMobile ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch',
        ...(isMobile ? {
          marginLeft: '-1rem',
          marginRight: '-1rem',
          paddingLeft: '1rem',
          paddingRight: '1rem'
        } : {})
      }}>
        {(['suit', 'skis', 'shoes', 'bindings'] as EquipmentType[]).map(type => (
          <button
            key={type}
            onClick={() => setActiveTab(type)}
            style={{
              padding: isMobile ? '0.3rem 0.45rem' : '0.75rem 1.5rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === type ? (isMobile ? '2px solid var(--accent-orange)' : '3px solid var(--accent-orange)') : (isMobile ? '2px solid transparent' : '3px solid transparent'),
              color: activeTab === type ? 'var(--accent-orange)' : 'var(--text-gray)',
              fontFamily: 'Poppins',
              fontSize: isMobile ? '0.6rem' : '1rem',
              fontWeight: activeTab === type ? 900 : 600,
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              letterSpacing: '-0.02em',
            }}
          >
            {type === 'skis' ? 'SKIS' : type === 'shoes' ? 'SHOES' : type === 'bindings' ? 'BINDINGS' : type.charAt(0).toUpperCase() + type.slice(1) + 's'}
          </button>
        ))}
      </div>

      <DataSelectionSection>
        <SelectionGrid>
          <SelectionGroup>
            <DateRangeDropdown
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onQuickDateRange={handleQuickDateRange}
              onUpdate={handleUpdateDate}
              formatDateForInput={formatDateForInput}
            />
          </SelectionGroup>

          {!isJumperMode && (
            <SelectionGroup>
              <AthletesDropdown
                athletes={athletes}
                selectedAthletes={selectedAthletes}
                onAthleteToggle={handleAthleteToggle}
                onSave={handleAthletesSave}
              />
            </SelectionGroup>
          )}

          <SelectionGroup>
            <SessionTypesDropdown
              selectedSessionTypes={selectedSessionTypes}
              onSessionTypeToggle={handleSessionTypeToggle}
              onSave={handleSessionTypesSave}
            />
          </SelectionGroup>

          <SelectionGroup>
            <LocationDropdown
              locations={locations}
              selectedLocation={selectedLocation}
              onLocationChange={setSelectedLocation}
            />
          </SelectionGroup>

          <SelectionGroup>
            <HillSizeDropdown
              hillSizes={hillSizes}
              selectedHillSize={selectedHillSize}
              onHillSizeChange={setSelectedHillSize}
            />
          </SelectionGroup>

          <SelectionGroup>
            <EquipmentDropdown
              equipment={filteredEquipment}
              selectedEquipment={selectedEquipment}
              onEquipmentToggle={handleEquipmentToggle}
              onSave={handleEquipmentSave}
              athletes={athletes}
              label={(() => {
                const base = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
                // Don't add 's' if the word already ends with 's' (skis, shoes, bindings)
                return base.endsWith('s') ? base : base + 's';
              })()}
            />
          </SelectionGroup>
        </SelectionGrid>
      </DataSelectionSection>

      {/* Charts Section */}
      {/* Full width: Average Distance Over Time */}
      <GraphSectionWithPeriodSelector style={{ marginBottom: '2rem' }}>
        <GraphTitle>Average Distance Over Time</GraphTitle>
        {startDate && endDate && selectedEquipment.length > 0 && selectedSessionTypes.length > 0 ? (
          <>
            {(selectedPeriod === 'daily' && distanceDataDaily.length > 0) ||
             (selectedPeriod === 'weekly' && distanceDataWeekly.length > 0) ||
             (selectedPeriod === 'monthly' && distanceDataMonthly.length > 0) ? (
              <>
                <PeriodSelectorTopRight>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPeriod === 'daily'}
                      onClick={() => setSelectedPeriod('daily')}
                    >
                      D
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Daily</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPeriod === 'weekly'}
                      onClick={() => setSelectedPeriod('weekly')}
                    >
                      W
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Weekly</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPeriod === 'monthly'}
                      onClick={() => setSelectedPeriod('monthly')}
                    >
                      M
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Monthly</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                </PeriodSelectorTopRight>
                <GraphContainer>
                  <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                    <LineChart data={
                      selectedPeriod === 'daily' ? distanceDataDaily :
                      selectedPeriod === 'weekly' ? distanceDataWeekly :
                      distanceDataMonthly
                    }>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey={selectedPeriod === 'daily' ? 'date' : selectedPeriod === 'weekly' ? 'week' : 'month'}
                    stroke="#363636"
                    style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                    tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                    tickFormatter={(value) => {
                      if (selectedPeriod === 'daily') {
                        const date = new Date(value);
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      } else if (selectedPeriod === 'weekly') {
                        const date = new Date(value);
                        const weekEnd = new Date(date);
                        weekEnd.setDate(weekEnd.getDate() + 6);
                        return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                      } else {
                        return value;
                      }
                    }}
                  />
                  <YAxis
                    stroke="#363636"
                    style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                    tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                    label={{ value: 'Distance (m)', angle: -90, position: 'insideLeft', style: { fontSize: '0.7rem', fontWeight: 900 } }}
                    domain={calculateYAxisDomain(
                      selectedPeriod === 'daily' ? distanceDataDaily :
                      selectedPeriod === 'weekly' ? distanceDataWeekly :
                      distanceDataMonthly,
                      selectedEquipment.map(eqId => {
                        const eq = filteredEquipment.find(e => String(e._id) === eqId);
                        return eq?.name || '';
                      }).filter(Boolean)
                    )}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--bg-primary)',
                      border: '1px solid var(--card-border)',
                      borderRadius: '8px',
                      fontFamily: 'Poppins',
                    }}
                  />
                  <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.9rem' }} iconSize={isMobile ? 8 : 12} />
                  {selectedEquipment.map((equipmentId, index) => {
                    const equipmentItem = filteredEquipment.find(eq => String(eq._id) === equipmentId);
                    if (!equipmentItem) return null;
                    const colors = getEquipmentColors();
                    return (
                      <Line
                        key={equipmentId}
                        type="monotone"
                        dataKey={equipmentItem.name}
                        stroke={colors[index % colors.length]}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        connectNulls={true}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            </GraphContainer>
              </>
            ) : (
              <EmptyState>
                No distance data available for the selected filters.
              </EmptyState>
            )}
          </>
        ) : (
          <EmptyState>
            {selectedEquipment.length === 0
              ? 'Please select at least one equipment to view distance data.'
              : !startDate || !endDate
              ? 'Please select a date range to view distance data.'
              : 'No distance data available for the selected filters.'}
          </EmptyState>
        )}
      </GraphSectionWithPeriodSelector>

      {/* Full width: Average Number of Jumps */}
      <GraphSectionWithPeriodSelector style={{ marginBottom: '2rem' }}>
        <GraphTitle>Average Number of Jumps</GraphTitle>
        {startDate && endDate && selectedEquipment.length > 0 && selectedSessionTypes.length > 0 ? (
          <>
            {(selectedPeriod === 'daily' && jumpCountDataDaily.length > 0) ||
             (selectedPeriod === 'weekly' && jumpCountDataWeekly.length > 0) ||
             (selectedPeriod === 'monthly' && jumpCountDataMonthly.length > 0) ? (
              <>
                <PeriodSelectorTopRight>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPeriod === 'daily'}
                      onClick={() => setSelectedPeriod('daily')}
                    >
                      D
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Daily</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPeriod === 'weekly'}
                      onClick={() => setSelectedPeriod('weekly')}
                    >
                      W
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Weekly</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPeriod === 'monthly'}
                      onClick={() => setSelectedPeriod('monthly')}
                    >
                      M
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Monthly</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                </PeriodSelectorTopRight>
                <GraphContainer>
                  <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                    <LineChart data={
                      selectedPeriod === 'daily' ? jumpCountDataDaily :
                      selectedPeriod === 'weekly' ? jumpCountDataWeekly :
                      jumpCountDataMonthly
                    }>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey={selectedPeriod === 'daily' ? 'date' : selectedPeriod === 'weekly' ? 'week' : 'month'}
                  stroke="#363636"
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                  tickFormatter={(value) => {
                    if (selectedPeriod === 'daily') {
                      const date = new Date(value);
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    } else if (selectedPeriod === 'weekly') {
                      const date = new Date(value);
                      const weekEnd = new Date(date);
                      weekEnd.setDate(weekEnd.getDate() + 6);
                      return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                    } else {
                      return value;
                    }
                  }}
                />
                <YAxis
                  stroke="#363636"
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                  label={{ value: 'Number of Jumps', angle: -90, position: 'insideLeft', style: { fontSize: '0.7rem', fontWeight: 900 } }}
                  domain={calculateYAxisDomain(
                    selectedPeriod === 'daily' ? jumpCountDataDaily :
                    selectedPeriod === 'weekly' ? jumpCountDataWeekly :
                    jumpCountDataMonthly,
                    selectedEquipment.map(eqId => {
                      const eq = filteredEquipment.find(e => String(e._id) === eqId);
                      return eq?.name || '';
                    }).filter(Boolean)
                  )}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--bg-primary)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '8px',
                    fontFamily: 'Poppins',
                  }}
                />
                <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.9rem' }} iconSize={isMobile ? 8 : 12} />
                {selectedEquipment.map((equipmentId, index) => {
                  const equipmentItem = filteredEquipment.find(eq => String(eq._id) === equipmentId);
                  if (!equipmentItem) return null;
                  const colors = getEquipmentColors();
                  return (
                    <Line
                      key={equipmentId}
                      type="monotone"
                      dataKey={equipmentItem.name}
                      stroke={colors[index % colors.length]}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      connectNulls={true}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </GraphContainer>
              </>
            ) : (
              <EmptyState>
                No jump count data available for the selected filters.
              </EmptyState>
            )}
          </>
        ) : (
          <EmptyState>
            {selectedEquipment.length === 0
              ? 'Please select at least one equipment to view jump count data.'
              : !startDate || !endDate
              ? 'Please select a date range to view jump count data.'
              : 'No jump count data available for the selected filters.'}
          </EmptyState>
        )}
      </GraphSectionWithPeriodSelector>

      {/* Average Speed Line Chart - Only for SKIS tab */}
      {activeTab === 'skis' && (
        <GraphSectionWithPeriodSelector style={{ marginBottom: '2rem' }}>
          <GraphTitle>Average Speed</GraphTitle>
          {startDate && endDate && selectedEquipment.length > 0 && selectedSessionTypes.length > 0 ? (
            <>
              {(selectedPeriod === 'daily' && speedDataDaily.length > 0) ||
               (selectedPeriod === 'weekly' && speedDataWeekly.length > 0) ||
               (selectedPeriod === 'monthly' && speedDataMonthly.length > 0) ? (
                <>
                  <PeriodSelectorTopRight>
                    <PeriodButtonWrapperSmall>
                      <PeriodButtonSmall
                        $isSelected={selectedPeriod === 'daily'}
                        onClick={() => setSelectedPeriod('daily')}
                      >
                        D
                      </PeriodButtonSmall>
                      <PeriodLabelSmall>Daily</PeriodLabelSmall>
                    </PeriodButtonWrapperSmall>
                    <PeriodButtonWrapperSmall>
                      <PeriodButtonSmall
                        $isSelected={selectedPeriod === 'weekly'}
                        onClick={() => setSelectedPeriod('weekly')}
                      >
                        W
                      </PeriodButtonSmall>
                      <PeriodLabelSmall>Weekly</PeriodLabelSmall>
                    </PeriodButtonWrapperSmall>
                    <PeriodButtonWrapperSmall>
                      <PeriodButtonSmall
                        $isSelected={selectedPeriod === 'monthly'}
                        onClick={() => setSelectedPeriod('monthly')}
                      >
                        M
                      </PeriodButtonSmall>
                      <PeriodLabelSmall>Monthly</PeriodLabelSmall>
                    </PeriodButtonWrapperSmall>
                  </PeriodSelectorTopRight>
                  <GraphContainer>
                    <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                      <LineChart data={
                        selectedPeriod === 'daily' ? speedDataDaily :
                        selectedPeriod === 'weekly' ? speedDataWeekly :
                        speedDataMonthly
                      }>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey={selectedPeriod === 'daily' ? 'date' : selectedPeriod === 'weekly' ? 'week' : 'month'}
                    stroke="#363636"
                    style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                    tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                    tickFormatter={(value) => {
                      if (selectedPeriod === 'daily') {
                        try {
                          const date = new Date(value);
                          if (isNaN(date.getTime())) return value;
                          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        } catch {
                          return value;
                        }
                      } else if (selectedPeriod === 'weekly') {
                        try {
                          const date = new Date(value);
                          if (isNaN(date.getTime())) return value;
                          const weekEnd = new Date(date);
                          weekEnd.setDate(weekEnd.getDate() + 6);
                          return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                        } catch {
                          return value;
                        }
                      } else {
                        return value;
                      }
                    }}
                  />
                  <YAxis
                    stroke="#363636"
                    style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                    tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                    label={{ value: 'Speed (km/h)', angle: -90, position: 'insideLeft', style: { fontSize: '0.7rem', fontWeight: 900 } }}
                    domain={(() => {
                      const currentSpeedData = selectedPeriod === 'daily' ? speedDataDaily :
                                                selectedPeriod === 'weekly' ? speedDataWeekly :
                                                speedDataMonthly;
                      // Calculate custom domain with more range
                      let minValue = Infinity;
                      let maxValue = -Infinity;
                      
                      currentSpeedData.forEach(point => {
                        selectedEquipment.forEach(eqId => {
                          const eq = filteredEquipment.find(e => String(e._id) === eqId);
                          if (eq) {
                            const value = point[eq.name];
                            if (value !== null && value !== undefined && typeof value === 'number') {
                              minValue = Math.min(minValue, value);
                              maxValue = Math.max(maxValue, value);
                            }
                          }
                        });
                      });
                      
                      if (minValue === Infinity || maxValue === -Infinity) {
                        return [0, 'auto'];
                      }
                      
                      // Add more range: subtract 3-5 from min, add 3-5 to max
                      const range = maxValue - minValue;
                      const padding = Math.max(3, range * 0.1); // At least 3, or 10% of range
                      const minDomain = Math.max(0, minValue - padding);
                      const maxDomain = maxValue + padding;
                      
                      return [minDomain, maxDomain];
                    })()}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--bg-primary)',
                      border: '1px solid var(--card-border)',
                      borderRadius: '8px',
                      fontFamily: 'Poppins',
                    }}
                  />
                  <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.9rem' }} iconSize={isMobile ? 8 : 12} />
                  {selectedEquipment.map((equipmentId, index) => {
                    const equipmentItem = filteredEquipment.find(eq => String(eq._id) === equipmentId);
                    if (!equipmentItem) return null;
                    const colors = getEquipmentColors();
                    return (
                      <Line
                        key={equipmentId}
                        type="monotone"
                        dataKey={equipmentItem.name}
                        stroke={colors[index % colors.length]}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        connectNulls={true}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            </GraphContainer>
                </>
              ) : (
                <EmptyState>
                  No speed data available for the selected filters.
                </EmptyState>
              )}
            </>
          ) : (
            <EmptyState>
              {selectedEquipment.length === 0
                ? 'Please select at least one equipment to view speed data.'
                : !startDate || !endDate
                ? 'Please select a date range to view speed data.'
                : 'No speed data available for the selected filters.'}
            </EmptyState>
          )}
        </GraphSectionWithPeriodSelector>
      )}

      {/* Longest Distances List - Full Width */}
      <GraphSection style={{ marginBottom: '2rem' }}>
        <GraphTitle>Longest Distances by Equipment</GraphTitle>
        {startDate && endDate && selectedEquipment.length > 0 && selectedSessionTypes.length > 0 && longestDistances.length > 0 ? (
          <div style={{ padding: '1rem', overflowX: 'auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
              gap: '1rem',
              padding: '1rem',
              background: 'var(--bg-secondary)',
              borderRadius: 'var(--radius-sm)',
              marginBottom: '0.5rem',
              fontFamily: 'Poppins',
              fontWeight: 700,
              fontSize: '0.9rem',
              color: 'var(--text-dark)',
              minWidth: '600px',
            }}>
              <div>Equipment</div>
              <div>Distance (m)</div>
              <div>Date</div>
              <div>Location</div>
              <div>Athlete</div>
            </div>
            {longestDistances.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                  gap: '1rem',
                  padding: '1rem',
                  background: index % 2 === 0 ? 'var(--bg-primary)' : 'var(--bg-secondary)',
                  borderRadius: 'var(--radius-sm)',
                  marginBottom: '0.5rem',
                  fontFamily: 'Poppins',
                  fontSize: '0.9rem',
                  color: 'var(--text-dark)',
                  minWidth: '600px',
                }}
              >
                <div style={{ fontWeight: 600 }}>{item.equipmentName}</div>
                <div>{item.distance.toFixed(1)}</div>
                <div>{new Date(item.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</div>
                <div>{item.location}</div>
                <div>{item.athlete}</div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState>No distance data available for the selected filters.</EmptyState>
        )}
      </GraphSection>

      {/* Number of Jumps by Equipment - Below Longest Distances */}
      <GraphSection>
          <GraphTitle>Number of Jumps by Equipment</GraphTitle>
          {selectedAthletes.length > 0 && filteredEquipment.length > 0 ? (
            <div style={{ padding: '1rem' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr',
                gap: '1rem',
                padding: '1rem',
                background: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-sm)',
                marginBottom: '0.5rem',
                fontFamily: 'Poppins',
                fontWeight: 700,
                fontSize: '0.9rem',
                color: 'var(--text-dark)',
              }}>
                <div>Equipment</div>
                <div>Jumps</div>
              </div>
              {filteredEquipment
                .filter(eq => selectedEquipment.length === 0 || selectedEquipment.includes(String(eq._id)))
                .map((item, index) => {
                  const jumpCount = jumpCounts[String(item._id)] || 0;
                  return (
                    <div
                      key={item._id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr',
                        gap: '1rem',
                        padding: '1rem',
                        background: index % 2 === 0 ? 'var(--bg-primary)' : 'var(--bg-secondary)',
                        borderRadius: 'var(--radius-sm)',
                        marginBottom: '0.5rem',
                        fontFamily: 'Poppins',
                        fontSize: '0.9rem',
                        color: 'var(--text-dark)',
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{item.name}</div>
                      <div>{jumpCount}</div>
                    </div>
                  );
                })}
              {filteredEquipment.filter(eq => selectedEquipment.length === 0 || selectedEquipment.includes(String(eq._id))).length === 0 && (
                <EmptyState>No equipment selected or available.</EmptyState>
              )}
            </div>
          ) : (
            <EmptyState>
              {selectedAthletes.length === 0
                ? 'Please select at least one athlete to view jump counts.'
                : 'No equipment available for the selected athletes.'}
            </EmptyState>
          )}
        </GraphSection>
    </AnalyticsContainer>
  );
};

export default EquipmentAnalytics;
